import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { admin } from '../api';

function AdminDashboard() {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [disponibilites, setDisponibilites] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateDispo, setShowCreateDispo] = useState(false);
  const [newDispo, setNewDispo] = useState({
    date: '',
    heure_debut: '',
    heure_fin: '',
    places_total: 4,
    lieu: 'Sint-Idesbald'
  });

  useEffect(() => {
    loadReservations();
  }, [filter]);

  const loadReservations = async () => {
    try {
      const response = await admin.getAllReservations({ statut: filter });
      setReservations(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des réservations');
    }
  };

  const handleValidate = async (id) => {
    if (!window.confirm('Confirmer cette réservation ?')) return;

    try {
      await admin.validateReservation(id);
      setSuccess('✅ Réservation confirmée ! Email envoyé au client.');
      await loadReservations();
    } catch (err) {
      setError('Erreur lors de la validation');
    }
  };

  const handleReject = async (id) => {
    const raison = prompt('Raison du refus (optionnel):');
    
    try {
      await admin.rejectReservation(id, raison);
      setSuccess('❌ Réservation refusée. Email envoyé au client.');
      await loadReservations();
    } catch (err) {
      setError('Erreur lors du refus');
    }
  };

  const handleCreateDispo = async (e) => {
    e.preventDefault();
    
    try {
      await admin.createDisponibilite(newDispo);
      setSuccess('✅ Disponibilité créée !');
      setShowCreateDispo(false);
      setNewDispo({
        date: '',
        heure_debut: '',
        heure_fin: '',
        places_total: 4,
        lieu: 'Sint-Idesbald'
      });
    } catch (err) {
      setError('Erreur lors de la création');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ color: 'white' }}>⚙️ Administration ManaQuest</h1>
        <button onClick={handleLogout} className="btn btn-danger">
          🚪 Déconnexion
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Réservations */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2>📋 Réservations</h2>
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
            >
              <option value="">Toutes</option>
              <option value="pending">En attente</option>
              <option value="confirmed">Confirmées</option>
              <option value="cancelled">Annulées</option>
            </select>
          </div>

          {reservations.length === 0 ? (
            <p style={{ color: '#666' }}>Aucune réservation</p>
          ) : (
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {reservations.map(res => (
                <div 
                  key={res.id}
                  style={{ 
                    padding: '15px', 
                    border: '2px solid #ddd', 
                    borderRadius: '8px',
                    marginBottom: '15px',
                    background: res.statut === 'pending' ? '#fff3cd' : '#fff'
                  }}
                >
                  <div style={{ marginBottom: '10px' }}>
                    <strong style={{ fontSize: '18px' }}>
                      {res.prenom} {res.nom}
                    </strong>
                    <p style={{ margin: '5px 0', color: '#666' }}>
                      📧 {res.email} | 📱 {res.whatsapp || 'N/A'}
                    </p>
                  </div>

                  <hr style={{ margin: '10px 0' }} />

                  <p><strong>📅 Date:</strong> {new Date(res.date).toLocaleDateString('fr-FR')}</p>
                  <p><strong>🕐 Horaire:</strong> {res.heure_debut} - {res.heure_fin}</p>
                  <p><strong>📍 Lieu:</strong> {res.lieu}</p>
                  <p><strong>👥 Places:</strong> {res.nb_places}</p>
                  
                  {res.message && (
                    <p><strong>💬 Message:</strong> {res.message}</p>
                  )}

                  <p style={{ marginTop: '10px' }}>
                    <strong>Statut:</strong>{' '}
                    <span style={{ 
                      padding: '4px 8px', 
                      borderRadius: '4px',
                      background: res.statut === 'confirmed' ? '#d4edda' : 
                                 res.statut === 'pending' ? '#fff3cd' : '#f8d7da'
                    }}>
                      {res.statut === 'pending' && '⏳ En attente'}
                      {res.statut === 'confirmed' && '✅ Confirmée'}
                      {res.statut === 'cancelled' && '❌ Annulée'}
                    </span>
                  </p>

                  {res.statut === 'pending' && (
                    <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                      <button 
                        onClick={() => handleValidate(res.id)}
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                      >
                        ✅ Valider
                      </button>
                      <button 
                        onClick={() => handleReject(res.id)}
                        className="btn btn-danger"
                        style={{ flex: 1 }}
                      >
                        ❌ Refuser
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Gestion des disponibilités */}
        <div className="card">
          <h2>📅 Créer une Disponibilité</h2>
          
          <form onSubmit={handleCreateDispo}>
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                value={newDispo.date}
                onChange={(e) => setNewDispo({ ...newDispo, date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div className="form-group">
                <label>Heure début *</label>
                <input
                  type="time"
                  value={newDispo.heure_debut}
                  onChange={(e) => setNewDispo({ ...newDispo, heure_debut: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Heure fin *</label>
                <input
                  type="time"
                  value={newDispo.heure_fin}
                  onChange={(e) => setNewDispo({ ...newDispo, heure_fin: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Nombre de places *</label>
              <input
                type="number"
                min="1"
                max="8"
                value={newDispo.places_total}
                onChange={(e) => setNewDispo({ ...newDispo, places_total: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Lieu *</label>
              <input
                type="text"
                value={newDispo.lieu}
                onChange={(e) => setNewDispo({ ...newDispo, lieu: e.target.value })}
                required
              />
            </div>

            <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
              ➕ Créer la disponibilité
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;